// src/pages/ProfilePage.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Paper,
  Tabs,
  Tab,
  Alert,
  Breadcrumbs,
  Link,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Person as PersonIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  Payment as PaymentIcon,
  DeleteForever as DeleteForeverIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import ProfileCard from '../components/profile/ProfileCard';
import ProfileForm from '../components/profile/ProfileForm';
import SecuritySettings from '../components/profile/SecuritySettings';
import NotificationSettings from '../components/profile/NotificationSettings';
import PreferencesSettings from '../components/profile/PreferencesSettings';
import OrganizationSettings from '../components/profile/OrganizationSettings';
import BillingSettings from '../components/profile/BillingSettings';
import DeleteAccount from '../components/profile/DeleteAccount';

const ProfilePage = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState(0);

  // Redirigir si el usuario no está autenticado
  useEffect(() => {
    if (!loading && !user) {
      navigate('/auth/login');
    }
  }, [user, loading, navigate]);

  // Si está cargando o no hay usuario, mostrar pantalla de carga
  if (loading || !user) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <Typography>Cargando perfil...</Typography>
        </Box>
      </Container>
    );
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Función para verificar si el usuario puede gestionar organizaciones
  const canManageOrganization = () => {
    const userRole = user?.rol || user?.role;
    return ['admin', 'organizador', 'entrenador'].includes(userRole);
  };

  const tabs = [
    { label: 'Información Personal', icon: <PersonIcon />, component: ProfileForm },
    { label: 'Seguridad', icon: <SecurityIcon />, component: SecuritySettings },
    { label: 'Notificaciones', icon: <NotificationsIcon />, component: NotificationSettings },
    { label: 'Preferencias', icon: <SettingsIcon />, component: PreferencesSettings },
    ...(canManageOrganization() ? [{ label: 'Mi Organización', icon: <BusinessIcon />, component: OrganizationSettings }] : []),
    { label: 'Facturación', icon: <PaymentIcon />, component: BillingSettings },
    { label: 'Eliminar Cuenta', icon: <DeleteForeverIcon />, component: DeleteAccount },
  ];

  const ActiveComponent = tabs[activeTab].component;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          underline="hover"
          color="inherit"
          href="/dashboard"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Dashboard
        </Link>
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
          <PersonIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Mi Perfil
        </Typography>
      </Breadcrumbs>

      {/* Título principal */}
      <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
        Mi Perfil
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Gestiona tu información personal y configuración de cuenta
      </Typography>

      <Grid container spacing={3}>
        {/* Sidebar con ProfileCard */}
        <Grid item xs={12} md={4}>
          <ProfileCard />
        </Grid>

        {/* Contenido principal */}
        <Grid item xs={12} md={8}>
          <Paper elevation={1} sx={{ height: 'fit-content' }}>
            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{ 
                  px: 2,
                  '& .MuiTabs-scrollButtons': {
                    '&.Mui-disabled': { opacity: 0.3 },
                  },
                  '& .MuiTabScrollButton-root': {
                    width: 48,
                    opacity: 0.8,
                    '&:hover': {
                      opacity: 1,
                      backgroundColor: 'action.hover',
                    },
                  },
                  '& .MuiTabs-indicator': {
                    height: 3,
                  },
                  '& .MuiTab-root': {
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      opacity: 1,
                    },
                    '&.Mui-selected': {
                      fontWeight: 600,
                    },
                  },
                }}
              >
                {tabs.map((tab, index) => (
                  <Tab
                    key={index}
                    icon={tab.icon}
                    label={tab.label}
                    iconPosition="start"
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.9rem',
                      minHeight: 48,
                      minWidth: { xs: 'auto', sm: 120 },
                      maxWidth: { xs: 200, sm: 'none' },
                      '& .MuiTab-iconWrapper': {
                        marginBottom: '0 !important',
                        marginRight: 1,
                      },
                      // Asegurar que el texto no se corte
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  />
                ))}
              </Tabs>
            </Box>

            {/* Contenido de la tab activa */}
            <Box sx={{ p: 3 }}>
              <ActiveComponent />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ProfilePage;